import { useEffect, useState } from 'react'
import { exportApi } from '@/api/endpoints'
import type { ExportRequest, ExportSummary } from '@/api/types'
import { Button, Card, Field, Input } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatBytes } from '@/utils/format'

/**
 * ZIP s nahranými doklady — pro účetní nebo k nahrání do Digitoo. Soubory
 * jsou pojmenované podle pohybu (datum_kód_firma_částka), takže se v nich dá
 * vyznat i mimo aplikaci.
 */
export default function ExportPage() {
  const [req, setReq] = useState<ExportRequest>({
    onlyNew: true,
    markExported: true,
    includeManifest: false,
  })
  const [summary, setSummary] = useState<ExportSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const body = (): ExportRequest => ({ ...req, from: req.from || undefined, to: req.to || undefined })

  useEffect(() => {
    setSummary(null)
    exportApi
      .summary(body())
      .then(setSummary)
      .catch((e) => setError(errorMessage(e, 'Nepodařilo se spočítat doklady.')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req.from, req.to, req.onlyNew])

  const download = async () => {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const { blob, fileName } = await exportApi.zip(body())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
      setDone(fileName)
      setSummary(await exportApi.summary(body()))
    } catch (e) {
      // Chyba z blob odpovědi má JSON uvnitř blobu — vytáhneme z něj hlášku.
      let message = 'Export se nezdařil.'
      const data = (e as { response?: { data?: Blob } }).response?.data
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).message ?? message
        } catch {
          /* není JSON */
        }
      }
      setError(message)
    } finally {
      setBusy(false)
    }
  }

  const toggle = (key: 'onlyNew' | 'markExported' | 'includeManifest') =>
    setReq((r) => ({ ...r, [key]: !r[key] }))

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Export dokladů (ZIP)</h1>
      <Card className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 md:max-w-md">
          <Field label="Pohyby od">
            <Input type="date" value={req.from ?? ''} onChange={(e) => setReq({ ...req, from: e.target.value })} />
          </Field>
          <Field label="Pohyby do">
            <Input type="date" value={req.to ?? ''} onChange={(e) => setReq({ ...req, to: e.target.value })} />
          </Field>
        </div>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={req.onlyNew} onChange={() => toggle('onlyNew')} />
            Jen doklady, které ještě nebyly v žádném ZIPu
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={req.markExported} onChange={() => toggle('markExported')} />
            Po stažení označit jako stažené v ZIPu
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={req.includeManifest} onChange={() => toggle('includeManifest')} />
            Přidat přehled <code className="text-xs">prehled.csv</code> (pro člověka; do Digitoo nenahrávat)
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-slate-100 pt-4">
          <Button onClick={download} disabled={busy || !summary || summary.fileCount === 0}>
            {busy ? 'Připravuji ZIP…' : 'Stáhnout ZIP'}
          </Button>
          <span className="text-sm text-slate-500">
            {summary
              ? summary.fileCount === 0
                ? 'Žádné doklady k exportu.'
                : `${summary.fileCount} souborů k ${summary.movementCount} pohybům, ${formatBytes(summary.totalBytes)}`
              : 'Počítám…'}
          </span>
        </div>
        {done && <p className="text-sm text-green-700">Staženo: {done}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </Card>
    </div>
  )
}
